import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import Swal from 'sweetalert2';

import { getRequest, postRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL } from 'Includes/urls';

const todayIso = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const emptyForm = () => ({
  from_date: todayIso(),
  to_date: todayIso(),
  reason: '',
  leave_type: 'leave',
});

const leaveTypeLabel = (value) => (value === 'work_from_home' ? 'Work from home' : 'Leave');

function MyLeaves(props) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState(emptyForm);

  const loadLeaves = () => {
    setLoading(true);
    getRequest(GET_URL.staff_leaves.api, { include_inactive: 1 }, props)
      .then((response) => {
        if (response && response.status === 200) {
          const data = response.data?.data || [];
          setRows(Array.isArray(data) ? data : []);
        }
      })
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    loadLeaves();
    // Load the signed-in user's leaves once when the page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markLeave = () => {
    if (!form.from_date || !form.to_date) {
      Swal.fire({ icon: 'warning', title: 'Select from and to dates' });
      return;
    }
    if (form.from_date > form.to_date) {
      Swal.fire({ icon: 'warning', title: 'To date must be on or after from date' });
      return;
    }
    setSaving(true);
    postRequest(POST_URL.staff_leaves.api, form, { ...props, return_error: true })
      .then((res) => {
        if (res?.status === 200 || res?.status === 201) {
          const isWorkFromHome = form.leave_type === 'work_from_home';
          Swal.fire({
            icon: 'success',
            title: isWorkFromHome ? 'Work from home marked' : 'Leave marked',
            text: form.from_date <= todayIso() && form.to_date >= todayIso()
              ? (isWorkFromHome
                ? 'Today’s work from home is now visible on the main dashboard.'
                : 'Today’s leave is now visible on the main dashboard.')
              : (isWorkFromHome ? 'Your work from home has been saved.' : 'Your leave has been saved.'),
            timer: 1800,
            showConfirmButton: false,
          });
          setForm(emptyForm());
          loadLeaves();
          return;
        }
        const detail = res?.data;
        const message = typeof detail === 'string'
          ? detail
          : detail?.detail
            || (Array.isArray(detail) ? detail.join(' ') : '')
            || Object.values(detail || {}).flat().join(' ')
            || 'Could not mark leave';
        Swal.fire({ icon: 'error', title: 'Could not mark leave', text: String(message) });
      })
      .finally(() => setSaving(false));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <EventBusyIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>My Leaves</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Mark leave or work from home. If today is included, everyone sees it on the main dashboard.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Mark leave or work from home</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="leave-type-label">Type</InputLabel>
              <Select
                labelId="leave-type-label"
                label="Type"
                value={form.leave_type}
                onChange={(e) => setForm((prev) => ({ ...prev, leave_type: e.target.value }))}
              >
                <MenuItem value="leave">Leave</MenuItem>
                <MenuItem value="work_from_home">Work from home</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="From date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.from_date}
              onChange={(e) => setForm((prev) => ({ ...prev, from_date: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="To date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.to_date}
              onChange={(e) => setForm((prev) => ({ ...prev, to_date: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Reason"
              fullWidth
              value={form.reason}
              onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant="contained"
              fullWidth
              disabled={saving}
              onClick={markLeave}
              sx={{ textTransform: 'none', height: 56 }}
            >
              {saving ? 'Saving…' : 'Mark'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {loading ? <CircularProgress size={28} sx={{ m: 2 }} /> : null}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f7fb' }}>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>From</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>To</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Alert severity="info" sx={{ my: 1 }}>You have not marked leave or work from home yet.</Alert>
                  </TableCell>
                </TableRow>
              ) : rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Chip
                      size="small"
                      label={leaveTypeLabel(row.leave_type)}
                      color={row.leave_type === 'work_from_home' ? 'info' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{row.from_date}</TableCell>
                  <TableCell>{row.to_date}</TableCell>
                  <TableCell>{row.reason || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status === 'cancelled' ? 'Cancelled' : 'Marked'}
                      color={row.status === 'cancelled' ? 'default' : 'warning'}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default MyLeaves;
